import {
  AvailableFilters,
  CatalogBrand,
  CatalogCustomAttribute,
  FilterSettings,
  Menus_MenuItemTag,
  ProductOption,
  TagEntry
} from 'src/apollo/onlineOrdering'
import { t } from 'src/translations'

import { matchByIdOrName } from './filterUtils'

export enum FilterType {
  Price = 'price',
  Availability = 'availability',
  Tags = 'tags',
  Option = 'option',
  CustomAttribute = 'customAttribute',
  Brand = 'brand',
  CountriesOfOrigin = 'countriesOfOrigin'
}

// A configurable filter group: all tags as one entry, each option/custom
// attribute as its own. Price/Availability are pinned ahead of these by
// FilterPanel/FilterBar.
export type OrderedFilterGroup =
  | { filterType: FilterType.Tags; tags: Menus_MenuItemTag[] }
  | { filterType: FilterType.Option; option: ProductOption }
  | {
      filterType: FilterType.CustomAttribute
      customAttribute: CatalogCustomAttribute
    }
  | { filterType: FilterType.Brand; brands: CatalogBrand[] }
  | { filterType: FilterType.CountriesOfOrigin; countryCodes: string[] }

// The section/dropdown heading for a group, shared by FilterBar and FilterPanel.
export const filterLabel = (group: OrderedFilterGroup): string => {
  switch (group.filterType) {
    case FilterType.Tags:
      return t('filters.tags')
    case FilterType.Option:
      return group.option.name
    case FilterType.CustomAttribute:
      return group.customAttribute.name
    case FilterType.Brand:
      return t('filters.brand')
    case FilterType.CountriesOfOrigin:
      return t('filters.countries-of-origin')
  }
}

// A tag matches an enabled entry by guid when both have one, else by name
const isTagEnabled = (
  tag: Menus_MenuItemTag,
  enabledTags: TagEntry[]
): boolean =>
  enabledTags.some((enabled) =>
    enabled.guid && tag.guid
      ? enabled.guid === tag.guid
      : enabled.name === tag.name
  )

// Filters the catalog to the tags/options in `filterSettings`, in strict ordinal order
export const orderFilterGroups = (
  availableFilters: AvailableFilters | null | undefined,
  filterSettings: FilterSettings | null | undefined
): OrderedFilterGroup[] | null => {
  if (!availableFilters || !filterSettings) {
    return null
  }

  const tagSettings = filterSettings.tagSettings
  const enabledTags = tagSettings?.tags ?? []
  const enabledOptions = filterSettings.optionSettings?.options ?? []
  const enabledCustomAttributes =
    filterSettings.customAttributeSettings?.customAttributes ?? []

  const groups: Array<{ ordinal: number; group: OrderedFilterGroup }> = []

  // only show the tags group is when at least one tag is actually enabled
  if (enabledTags.length > 0) {
    const filteredTags = availableFilters.tags.filter((tag) =>
      isTagEnabled(tag, enabledTags)
    )
    if (filteredTags.length > 0) {
      groups.push({
        ordinal: tagSettings?.ordinal ?? 0,
        group: { filterType: FilterType.Tags, tags: filteredTags }
      })
    }
  }

  availableFilters.productOptions.forEach((option) => {
    const enabled = enabledOptions.find((o) => o.name === option.name)
    if (enabled) {
      groups.push({
        ordinal: enabled.ordinal,
        group: { filterType: FilterType.Option, option }
      })
    }
  })

  availableFilters.customAttributes.forEach((customAttribute) => {
    const enabled = enabledCustomAttributes.find((enabledAttributeSetting) =>
      matchByIdOrName(
        {
          id: enabledAttributeSetting.guid,
          name: enabledAttributeSetting.name
        },
        customAttribute
      )
    )
    if (enabled) {
      groups.push({
        ordinal: enabled.ordinal,
        group: { filterType: FilterType.CustomAttribute, customAttribute }
      })
    }
  })

  const brandSettings = filterSettings.brandSettings
  if (brandSettings && availableFilters.brands.length > 0) {
    groups.push({
      ordinal: brandSettings.ordinal,
      group: { filterType: FilterType.Brand, brands: availableFilters.brands }
    })
  }

  const countriesOfOriginSettings = filterSettings.countriesOfOriginSettings
  const countryCodes = availableFilters.countriesOfOrigin?.countryCodes ?? []
  if (countriesOfOriginSettings && countryCodes.length > 0) {
    groups.push({
      ordinal: countriesOfOriginSettings.ordinal,
      group: { filterType: FilterType.CountriesOfOrigin, countryCodes }
    })
  }

  return groups.sort((a, b) => a.ordinal - b.ordinal).map((g) => g.group)
}
